import createClient from "openapi-fetch";

import type { paths } from "./openapi";
import type {
  AdminUser,
  AdminDeployment,
  AdminReleaseList,
  AudioManifest,
  AuthInfo,
  AuthMode,
  CursorPage,
  DeviceToken,
  JobStatus,
  LoginResponse,
  Paper,
  PaperStatus,
  QueueStatus,
  Settings,
  FetchGroup,
  FetchGroupProposal,
  StatusReceipt,
  SyncStatus,
  VoiceIntent,
} from "./types";

export const PAGE_SIZE = 20;

export const generatedClient = createClient<paths>({ credentials: "include" });

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

let csrfToken = "";

export function setCsrfToken(value?: string): void {
  csrfToken = value ?? "";
}

export function clearSessionSecrets(): void {
  csrfToken = "";
}

function isUnsafe(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

function messageFrom(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object" && "detail" in payload) {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? "GET";
  const headers = new Headers(init.headers);
  if (csrfToken && isUnsafe(method)) headers.set("X-CSRF-Token", csrfToken);
  if (typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(path, { ...init, method, headers, credentials: "include" });
  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => "");
  if (!response.ok) {
    throw new ApiError(response.status, messageFrom(payload, response.statusText), payload);
  }
  return payload as T;
}

function query(params: Record<string, string | number | undefined | null>): string {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") out.set(key, String(value));
  }
  const rendered = out.toString();
  return rendered ? `?${rendered}` : "";
}

function normalizePage<T>(value: CursorPage<T>): CursorPage<T> {
  return {
    items: Array.isArray(value.items) ? value.items : [],
    next_cursor: value.next_cursor ?? null,
    total: value.total,
    revision: value.revision,
  };
}

export const api = {
  authMode: () => request<AuthMode>("/api/auth/mode"),

  authCheck: async () => {
    const info = await request<AuthInfo>("/api/auth/session");
    setCsrfToken(info.csrf_token);
    return info;
  },

  login: async (input: { username?: string; password: string; multiuser: boolean }) => {
    const result = await request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username: input.username?.trim() ?? "", password: input.password }),
    });
    setCsrfToken(result.csrf_token);
    return api.authCheck();
  },

  logout: async () => {
    try { await request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }); }
    finally { clearSessionSecrets(); }
  },

  papers: async (input: {
    status: QueueStatus;
    cursor?: string | null;
    limit?: number;
    minScore?: number;
    signal?: AbortSignal;
  }) => {
    if (input.status === "todo") {
      const result = await request<CursorPage<Paper>>(`/api/todo${query({
        cursor: input.cursor,
        limit: input.limit ?? PAGE_SIZE,
      })}`, { signal: input.signal });
      return normalizePage(result);
    }
    const result = await request<CursorPage<Paper>>(`/api/papers${query({
      status: input.status,
      cursor: input.cursor,
      limit: input.limit ?? PAGE_SIZE,
      min_score: input.minScore ?? 0,
    })}`, { signal: input.signal });
    return normalizePage(result);
  },

  paper: (paperId: number) => request<Paper>(`/api/papers/${paperId}`),

  updateStatus: (paper: Paper, status: PaperStatus, actionId: string) =>
    request<StatusReceipt>(`/api/papers/${paper.id}/status`, {
      method: "POST",
      body: JSON.stringify({
        action_id: actionId,
        expected_status: paper.status,
        status,
      }),
    }),

  undo: (actionId: string) => request<{ ok: boolean }>(`/api/actions/${encodeURIComponent(actionId)}/undo`, {
    method: "POST",
  }),

  restore: (paper: Paper, actionId: string) => request<{ ok: boolean; status: PaperStatus }>(
    `/api/papers/${paper.id}/restore`, {
      method: "POST",
      body: JSON.stringify({ action_id: actionId, expected_status: paper.status }),
    },
  ),

  setRead: (paper: Paper, read: boolean) => request<{ ok: boolean; read_at?: string | null }>(
    `/api/papers/${paper.id}/read`, { method: "POST", body: JSON.stringify({ read }) },
  ),

  openPaper: (paperId: number) => request<{ link?: string }>(`/api/papers/${paperId}/open`, {
    method: "POST",
  }),

  search: (text: string, signal?: AbortSignal) => request<Paper[]>(
    `/api/arxiv/search${query({ q: text, max_results: 30 })}`, { signal },
  ),

  addPaper: (arxivId: string) => request<{ ok: boolean }>("/api/papers/add", {
    method: "POST", body: JSON.stringify({ arxiv_id: arxivId }),
  }),

  settings: () => request<Settings>("/api/settings"),
  audioCache: () => request<{ bytes: number; assets: number; reclaimable_bytes: number; limit_bytes: number; backup_bytes: number }>("/api/audio/cache"),
  clearAudioCache: () => request<{ tracks: number; assets: number; files: number; evicted: number; bytes: number; limit_bytes: number }>("/api/audio/cache/clear", { method: "POST" }),
  workspaceSettings: () => request<Settings>("/api/workspace/settings"),
  saveWorkspaceSettings: (settings: Partial<Settings>) => request<{ ok: boolean; revision: number }>("/api/workspace/settings", {
    method: "PUT", body: JSON.stringify(settings),
  }),
  testWorkspaceSetting: (service: string) => request<{ ok: boolean; message?: string }>(
    `/api/workspace/settings/test${query({ service })}`, { method: "POST" },
  ),
  saveSettings: (settings: Partial<Settings>) => request<{ ok: boolean }>("/api/settings", {
    method: "PUT", body: JSON.stringify(settings),
  }),
  testSetting: (service: string) => request<{ ok: boolean; message?: string }>(
    `/api/settings/test${query({ service })}`, { method: "POST" },
  ),
  saveFetchGroup: (group: FetchGroup) => request<{ ok: boolean; id?: number; group_uuid?: string; slug?: string; version?: number }>(
    group.group_uuid || group.id ? `/api/fetch_groups/${group.group_uuid || group.id}` : "/api/fetch_groups",
    { method: group.group_uuid || group.id ? "PUT" : "POST", body: JSON.stringify((group.group_uuid || group.id) && group.version !== undefined ? { ...group, expected_version: group.version } : group) },
  ),
  saveWorkspaceFetchGroup: (group: FetchGroup) => request<{ ok: boolean; id?: number; group_uuid?: string; slug?: string; version?: number }>(
    group.group_uuid || group.id ? `/api/workspace/fetch_groups/${group.group_uuid || group.id}` : "/api/workspace/fetch_groups",
    { method: group.group_uuid || group.id ? "PUT" : "POST", body: JSON.stringify((group.group_uuid || group.id) && group.version !== undefined ? { ...group, expected_version: group.version } : group) },
  ),
  deleteWorkspaceFetchGroup: (groupId: string | number, version?: number) => request<{ ok: boolean }>(`/api/workspace/fetch_groups/${groupId}${query(version === undefined ? {} : { expected_version: version })}`, {
    method: "DELETE",
  }),
  deleteFetchGroup: (groupId: string | number, version?: number) => request<{ ok: boolean }>(`/api/fetch_groups/${groupId}${query(version === undefined ? {} : { expected_version: version })}`, {
    method: "DELETE",
  }),
  fetchGroupNow: (groupId: string | number, workspace = false) => request<{ ok: boolean; queued: boolean; job_id: number; message?: string }>(
    `/api/${workspace ? "workspace/" : ""}fetch_groups/${groupId}/fetch`, { method: "POST" },
  ),
  fetchGroupHistory: (groupId: string | number, workspace = false) => request<{ items: Array<Record<string, unknown>>; next_cursor?: number | null }>(
    `/api/${workspace ? "workspace/" : ""}fetch_groups/${groupId}/history`,
  ),
  fetchGroups: (includeDeleted = false, workspace = false) => request<FetchGroup[]>(
    `/api/${workspace ? "workspace/" : ""}fetch_groups${query(includeDeleted ? { include_deleted: "true" } : {})}`,
  ),
  suggestFetchGroup: (input: { description: string; feedback?: string; previous_proposal?: FetchGroupProposal }, workspace = false) =>
    request<{ ok: boolean; queued: boolean; job_id: number }>(`/api/${workspace ? "workspace/" : ""}fetch_groups/suggestions`, {
      method: "POST", body: JSON.stringify(input),
    }),
  restoreFetchGroup: (groupUuid: string, workspace = false) => request<{ ok: boolean }>(
    `/api/${workspace ? "workspace/" : ""}fetch_groups/${groupUuid}/restore`, { method: "POST" },
  ),
  purgeFetchGroup: (groupUuid: string, workspace = false) => request<{ ok: boolean }>(
    `/api/${workspace ? "workspace/" : ""}fetch_groups/${groupUuid}/purge`, { method: "DELETE" },
  ),

  jobStatus: () => request<JobStatus>("/api/jobs/status"),
  job: async (jobId: number, workspace = false) => {
    const result = await request<JobStatus>(`/api/${workspace ? "workspace/" : ""}jobs/${jobId}`);
    return { ...result, running: ["queued", "running"].includes(result.state ?? "") };
  },
  runFetch: (date?: string) => request<{ ok: boolean; queued: boolean; job_id: number }>(
    `/api/jobs/run${query(date ? { date } : { smart: "true" })}`, { method: "POST" },
  ),

  syncStatus: () => request<SyncStatus>("/api/sync/status"),
  syncNow: () => request<{ ok: boolean; queued: boolean; job_id: number }>("/api/sync/now", {
    method: "POST",
  }),
  pairSync: (input: { server: string; username: string; password: string; device_name: string }) =>
    request<{
      ok: boolean;
      paired?: boolean;
      username?: string;
      device_name?: string;
      migration?: {
        settings_imported?: string[];
        groups_created?: string[];
        groups_renamed?: Array<{ group_uuid: string; old_slug: string; new_slug: string }>;
        default_replaced?: boolean;
        automatic_fetch_active?: boolean;
      };
    }>("/api/sync/pair", {
      method: "POST", body: JSON.stringify(input),
    }),
  unpairSync: () => request<{ ok: boolean }>("/api/sync/pair", { method: "DELETE" }),

  audioManifest: (paperId: number, stage: string) => request<AudioManifest>(
    `/api/papers/${paperId}/audio${query({ stage })}`,
  ),
  prepareAudio: (paperId: number, stage: string, priority = 100) => request<{ ok: boolean; queued?: number }>("/api/audio/prepare", {
    method: "POST",
    body: JSON.stringify({ paper_ids: [paperId], stage, priority }),
  }),
  retryAudio: (paperId: number, stage: "new" | "review") => request<{ ok: boolean; queued?: number }>(
    "/api/audio/retry", {
      method: "POST", body: JSON.stringify({ paper_id: paperId, stage }),
    },
  ),
  retryDeepSummary: (paperId: number) => request<{ ok: boolean; message?: string; job_id?: number }>(
    `/api/papers/${paperId}/deep_summary/retry`, { method: "POST" },
  ),
  retryAnnotations: (paperId: number) => request<{ ok: boolean; queued?: boolean; job_id?: number; job_type?: string }>(
    `/api/papers/${paperId}/annotations/retry`, { method: "POST" },
  ),
  voiceIntent: (text: string) => request<{ intent: VoiceIntent }>("/api/voice_intent", {
    method: "POST", body: JSON.stringify({ text }),
  }),
  transcribeAudio: (blob: Blob) => {
    const form = new FormData();
    const extension = blob.type.includes("wav")
      ? "wav"
      : blob.type.includes("mp4")
        ? "m4a"
        : blob.type.includes("ogg")
          ? "ogg"
          : blob.type.includes("mpeg") || blob.type.includes("mp3")
            ? "mp3"
            : "webm";
    form.append("audio", blob, `command.${extension}`);
    return request<{ ok: boolean; queued: boolean; job_id: number }>("/api/stt", { method: "POST", body: form });
  },
  audioBlob: async (path: string) => {
    const safePath = audioAssetPath({ url: path });
    if (!safePath) throw new ApiError(400, "音频地址不安全", null);
    const response = await fetch(safePath, { credentials: "include" });
    if (!response.ok) throw new ApiError(response.status, "音频下载失败", null);
    return response.blob();
  },

  adminLogin: async (username: string, password: string) => {
    const result = await request<LoginResponse>("/api/admin/auth/login", {
      method: "POST", body: JSON.stringify({ username: username.trim(), password }),
    });
    setCsrfToken(result.csrf_token);
    return api.adminSession();
  },
  adminSession: async () => {
    const info = await request<AuthInfo>("/api/admin/auth/session");
    setCsrfToken(info.csrf_token);
    return info;
  },
  adminLogout: async () => {
    try { await request<{ ok: boolean }>("/api/admin/auth/logout", { method: "POST" }); }
    finally { clearSessionSecrets(); }
  },
  adminUsers: () => request<AdminUser[]>("/api/admin/users"),
  createUser: (username: string, password: string, researchEnabled = true) => request<{ ok: boolean; username: string }>(
    "/api/admin/users", { method: "POST", body: JSON.stringify({ username, password, research_enabled: researchEnabled }) },
  ),
  setUserDisabled: (username: string, disabled: boolean) => request<{ ok: boolean }>(
    `/api/admin/users/${encodeURIComponent(username)}/disable`, {
      method: "POST", body: JSON.stringify({ disabled }),
    },
  ),
  resetUserPassword: (username: string, password: string) => request<{ ok: boolean }>(
    `/api/admin/users/${encodeURIComponent(username)}/reset_password`, {
      method: "POST", body: JSON.stringify({ password }),
    },
  ),
  setUserResearchEnabled: (username: string, researchEnabled: boolean) => request<{ ok: boolean }>(
    `/api/admin/users/${encodeURIComponent(username)}/research`, {
      method: "POST", body: JSON.stringify({ research_enabled: researchEnabled }),
    },
  ),
  adminReleases: () => request<AdminReleaseList>("/api/admin/releases"),
  adminDeployments: () => request<AdminDeployment[]>("/api/admin/deployments"),
  createDeployment: (version: string) => request<AdminDeployment>("/api/admin/deployments", {
    method: "POST", body: JSON.stringify({ version }),
  }),
  devices: () => request<{ devices: DeviceToken[] } | DeviceToken[]>("/api/auth/devices"),
  deleteDevice: (id: string) => request<{ ok: boolean }>(`/api/auth/devices/${encodeURIComponent(id)}`, {
    method: "DELETE",
  }),
  changePassword: (oldPassword: string, newPassword: string) => request<{ ok: boolean; sessions_revoked: number; devices_revoked: number; re_pair_required: boolean }>(
    "/api/auth/password", {
      method: "POST",
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    },
  ),
};

export function audioAssetPath(segment: { audio_uid?: string; url?: string }): string | null {
  if (segment.url) {
    const raw = segment.url.trim();
    if (!raw.startsWith("/") && !/^https?:\/\//i.test(raw)) return null;
    try {
      const url = new URL(raw, window.location.origin);
      if (!["http:", "https:"].includes(url.protocol)) return null;
      if (url.origin !== window.location.origin || url.username || url.password) return null;
      return `${url.pathname}${url.search}`;
    } catch {
      return null;
    }
  }
  if (segment.audio_uid) return `/api/audio/assets/${encodeURIComponent(segment.audio_uid)}`;
  return null;
}
